import {
  INLAY_ONLAY_RESTRICTED_TOOTH_IDS,
  SEALANTS_RESTRICTED_TOOTH_IDS,
  FURCATION_ALLOWED_TOOTH_IDS,
  VENEER_RESTRICTED_TOOTH_IDS,
  RETAINER_ALLOWED_TOOTH_IDS,
  LABEL_TO_CONDITION,
  CONDITION_CODES_WITH_SUB_ITEMS,
} from '@constants/Charting/conditions.schema'

/**
 * Helper function to check if a condition is applicable to the selected tooth
 */
export const isConditionApplicableToTooth = (
  conditionCode: string,
  selectedTooth: number | null,
): boolean => {
  // If no tooth is selected, show all conditions
  if (!selectedTooth) return true

  // Check restrictions based on condition type
  switch (conditionCode) {
    case 'inlay':
    case 'onlay':
      return !INLAY_ONLAY_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
    case 'sealants':
      return !SEALANTS_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
    case 'furcation':
      return FURCATION_ALLOWED_TOOTH_IDS.includes(selectedTooth)
    case 'veneer':
      return !VENEER_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
    case 'retainer':
      return RETAINER_ALLOWED_TOOTH_IDS.includes(selectedTooth)
    default:
      // All other conditions are applicable to all teeth
      return true
  }
}

/**
 * Get condition code from label
 */
export const getConditionFromLabel = (label: string): string => {
  return LABEL_TO_CONDITION[label] || label.toLowerCase()
}

/**
 * Helper function to capitalize first letter and lowercase the rest
 */
export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Helper function to determine if a condition has sub-items (materials, types, surfaces)
 */
export const conditionHasSubItems = (conditionCode: string): boolean => {
  return CONDITION_CODES_WITH_SUB_ITEMS.includes(conditionCode.toLowerCase())
}

/** Furcation area codes available for a tooth (upper/lower molar layout). */
export const getAvailableFurcationAreas = (
  toothNumber: number | null,
): string[] => {
  if (!toothNumber) return []
  const availableAreas: string[] = []
  if (toothNumber >= 1 && toothNumber <= 16) {
    availableAreas.push('palatal')
  }
  if (toothNumber >= 17 && toothNumber <= 32) {
    availableAreas.push('lingual')
  }
  if ([1, 2, 3, 14, 15, 16].includes(toothNumber)) {
    availableAreas.push('buccal-distal', 'buccal-mesial')
  } else if ([17, 18, 19, 30, 31, 32].includes(toothNumber)) {
    availableAreas.push('buccal')
  }
  return availableAreas
}

export const parseFurcationSurfaceCodes = (
  surface: string | null | undefined,
): string[] => {
  if (surface == null) {
    return []
  }
  return surface
    .split(',')
    .map((area) => area.trim())
    .filter(Boolean)
}

/** null surface = all available areas; '' = explicitly none. */
export const getSelectedFurcationAreas = (
  surface: string | null | undefined,
  toothNumber: number | null,
): string[] => {
  if (surface == null) {
    return getAvailableFurcationAreas(toothNumber)
  }
  return parseFurcationSurfaceCodes(surface)
}
